// src/components/BlogLayout.jsx
import React from "react";
import { Layout, Menu, Button, Avatar, Dropdown, Modal } from "antd";
import {
  UserOutlined,
  LoginOutlined,
  LogoutOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useBlogAPI";
import Footer from "./Footer";
import PageTransition from "./PageTransition";

const { Header, Content } = Layout;

// 获取用户头像的函数
const getUserAvatar = () => {
  const profile = localStorage.getItem("userProfile");
  if (profile) {
    try {
      const parsedProfile = JSON.parse(profile);
      return parsedProfile.avatarUrl;
    } catch (e) {
      return null;
    }
  }
  return null;
};

const BlogLayout = ({ children, user: propUser, onCreatePost }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, loading } = useAuth(); // 获取loading状态
  const [showAuthModal, setShowAuthModal] = React.useState(false);

  const currentUser = propUser !== undefined ? propUser : authUser;

  // 检查是否需要认证的操作
  const checkAuth = (actionName = "此操作") => {
    if (!loading && !currentUser) {
      // 确保在加载完成后再检查用户状态
      setShowAuthModal(true);
      return false;
    }
    return true;
  };

  const handleGoToLogin = () => {
    navigate("/auth");
    setShowAuthModal(false);
  };

  const handleCancel = () => {
    setShowAuthModal(false);
  };

  const handleCreatePost = () => {
    if (checkAuth("写文章")) {
      // 如果用户已登录或认证检查通过，执行传入的onCreatePost函数或导航到创建文章页面
      if (onCreatePost) {
        onCreatePost();
      } else {
        navigate("/posts/create");
      }
    }
  };

  const getSelectedKey = () => {
    if (location.pathname === "/") return "home";
    if (location.pathname === "/archive") return "archives";
    if (location.pathname === "/categories") return "categories";
    if (location.pathname === "/about") return "about";
    return "";
  };

  const menuItems = [
    {
      key: "home",
      label: <Link to="/">首页</Link>,
    },
    {
      key: "categories",
      label: <Link to="/categories">分类</Link>,
    },
    {
      key: "archives",
      label: <Link to="/archive">归档</Link>,
    },
    {
      key: "about",
      label: <Link to="/about">关于</Link>,
    },
  ];

  const userMenuItems = currentUser
    ? [
        {
          key: "profile",
          label: "个人资料",
          onClick: () => navigate("/about"),
        },
        {
          key: "my-posts",
          label: "我的文章",
          onClick: () => navigate("/archive"),
        },
        {
          key: "logout",
          label: "退出登录",
          icon: <LogoutOutlined />,
          onClick: () => {
            localStorage.removeItem("authToken");
            localStorage.removeItem("userProfile");
            window.location.reload();
          },
        },
      ]
    : [
        {
          key: "login",
          label: "登录/注册",
          icon: <LoginOutlined />,
          onClick: () => navigate("/auth"),
        },
      ];

  // 获取用户头像URL
  const userAvatarUrl = getUserAvatar();

  return (
    <>
      <Layout style={{ minHeight: "100vh" }}>
        <Header
          style={{ position: "sticky", top: 0, zIndex: 1, width: "100%" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <div
                className="logo"
                style={{
                  color: "white",
                  fontSize: 25,
                  fontWeight: "bold",
                }}
              >
                我的博客
              </div>

              <Menu
                theme="dark"
                mode="horizontal"
                items={menuItems}
                style={{
                  minWidth: 0,
                  justifyContent: "flex-start",
                  fontWeight: "normal",
                }}
                selectedKeys={[getSelectedKey()]}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              {currentUser ? (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreatePost}
                >
                  写文章
                </Button>
              ) : (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => checkAuth("写文章")}
                >
                  写文章
                </Button>
              )}

              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                {userAvatarUrl ? (
                  <Avatar
                    src={userAvatarUrl}
                    alt="用户头像"
                    style={{ cursor: "pointer" }}
                  />
                ) : (
                  <Avatar
                    icon={<UserOutlined />}
                    style={{ cursor: "pointer" }}
                  />
                )}
              </Dropdown>
            </div>
          </div>
        </Header>
        <Content
          style={{
            padding: "24px 0",
            backgroundColor: "#ffffff",
          }}
        >
          <PageTransition location={location}>{children}</PageTransition>
        </Content>
        <Footer />
      </Layout>

      {/* 未登录提示模态框 */}
      <Modal
        title="需要登录"
        open={showAuthModal}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            取消
          </Button>,
          <Button key="login" type="primary" onClick={handleGoToLogin}>
            去登录
          </Button>,
        ]}
      >
        <p>您需要先登录才能执行此操作。</p>
      </Modal>
    </>
  );
};

export default BlogLayout;
